import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import PageTransition from '@/components/PageTransition'
import { AuditErrorCard, AuditRunningCard } from '@/components/AuditStatusCards'
import { auditsApi } from '@/api/client'
import type { DeviceType } from '@/api/client'
import { getDesktopBridge, type LocalAuditOutcome } from '@/lib/desktop'

interface LocalAuditState {
  url: string
  device: DeviceType
}

type Stage =
  | { kind: 'running'; progress: number; message: string }
  | { kind: 'failed'; outcome: Extract<LocalAuditOutcome, { ok: false }> }
  | { kind: 'importFailed'; result: Extract<LocalAuditOutcome, { ok: true }> }

const CHROME_DOWNLOAD_URL = 'https://www.google.com/chrome/'

/**
 * Runs an audit on this machine through the desktop app, then imports the
 * result into the user's account and opens it. Desktop only.
 */
export default function LocalAuditPage() {
  const navigate = useNavigate()
  const request = useLocation().state as LocalAuditState | null
  const [stage, setStage] = useState<Stage>({ kind: 'running', progress: 0, message: 'Starting Chrome on this machine…' })
  const started = useRef(false)
  const pendingCancel = useRef<number | null>(null)
  // Guards the runLocalAudit().then() continuation and the import mutation's
  // callbacks against firing after a real unmount (navigating away while a
  // run is in flight): without this, a promise that resolves later would
  // still call navigate/setStage against a page the user has already left.
  const stale = useRef(false)

  const importResult = useMutation({
    mutationFn: (result: Extract<LocalAuditOutcome, { ok: true }>) =>
      auditsApi.importLocal({
        url: request!.url,
        device: request!.device,
        lhr: JSON.parse(result.lhr),
        reportHtml: result.reportHtml,
      }),
    onSuccess: ({ id }) => {
      if (stale.current) return
      navigate(`/reports/${id}`, { replace: true })
    },
    onError: (_error, result) => {
      if (stale.current) return
      setStage({ kind: 'importFailed', result })
    },
  })

  useEffect(() => {
    const bridge = getDesktopBridge()
    if (!bridge || !request) {
      navigate('/', { replace: true })
      return
    }
    // StrictMode mounts, unmounts and remounts immediately: a cancel scheduled
    // by that simulated unmount is withdrawn here, and `stale` is cleared in
    // the same place — synchronously, before any promise from the in-flight
    // run can resolve — so only a real navigation away cancels/stales the
    // run. `started` stops a second run from starting.
    if (pendingCancel.current !== null) {
      window.clearTimeout(pendingCancel.current)
      pendingCancel.current = null
    }
    stale.current = false
    const unsubscribe = bridge.onProgress(event =>
      setStage({ kind: 'running', progress: event.progress, message: event.message }),
    )
    if (!started.current) {
      started.current = true
      void bridge.runLocalAudit(request.url, request.device).then(outcome => {
        if (stale.current) return
        if (outcome.ok) {
          setStage({ kind: 'running', progress: 100, message: 'Saving to your account…' })
          importResult.mutate(outcome)
        } else if (outcome.code === 'CANCELLED') {
          navigate('/', { replace: true })
        } else {
          setStage({ kind: 'failed', outcome })
        }
      })
    }
    return () => {
      unsubscribe()
      stale.current = true
      pendingCancel.current = window.setTimeout(() => void bridge.cancel(), 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per page visit by design
  }, [])

  return (
    <PageTransition>
      <div className="max-w-3xl mx-auto px-4 py-12">
        {stage.kind === 'running' && (
          <AuditRunningCard
            message={stage.message}
            progress={stage.progress}
            onCancel={() => void getDesktopBridge()?.cancel()}
          />
        )}
        {stage.kind === 'failed' && (
          <AuditErrorCard message={stage.outcome.message} actionLabel="Back" onAction={() => navigate('/')}>
            {stage.outcome.code === 'CHROME_NOT_FOUND' && (
              <a href={CHROME_DOWNLOAD_URL} target="_blank" rel="noreferrer" className="text-sm text-accent underline">
                Download Google Chrome
              </a>
            )}
          </AuditErrorCard>
        )}
        {stage.kind === 'importFailed' && (
          <AuditErrorCard
            message="The audit ran, but saving it to your account failed."
            actionLabel="Try saving again"
            onAction={() => {
              setStage({ kind: 'running', progress: 100, message: 'Saving to your account…' })
              importResult.mutate(stage.result)
            }}
          />
        )}
      </div>
    </PageTransition>
  )
}
